/** Đặt lịch — giới hạn & nhắc lịch. Real /api/v1/clinic/settings/booking (per primary branch). */
import { apiClient } from "./client";

export interface BookingSettings {
  /** false = Không giới hạn, true = Có giới hạn. */
  limitEnabled: boolean;
  dailyTotalLimit: number | null;
  sessionLimitEnabled: boolean;
  /**
   * Giới hạn lượt theo từng buổi khám, khoá theo mã buổi (clinic_session.code),
   * VD: { MORNING: 25, AFTERNOON: 25, EVENING: 15 }. Buổi có mặt trong map = có giới hạn.
   */
  sessionLimits: Record<string, number>;
  /** Giới hạn lượt/người/ngày — phạm vi TỪNG BỆNH NHÂN, khác "Không giới hạn" (toàn phòng khám). */
  perPersonPerDayEnabled: boolean;
  perPersonPerDayLimit: number;
  startSequenceEnabled: boolean;
  startSequenceNumber: number | null;
  advanceMinDays: number;
  advanceMaxDays: number;
  reminderBeforeEnabled: boolean;
  reminderBeforeDays: number[];
  reminderAfterEnabled: boolean;
  reminderAfterDays: number[];
  /**
   * #19554 — nhắc lịch TÁI KHÁM trước N ngày, riêng với nhắc lịch hẹn. Không có công tắc ở đây: gửi
   * hay không do sự kiện "Nhắc lịch tái khám" ở Cài đặt → Thông báo. Vắng trên API cũ.
   */
  reminderFollowUpDays?: number[];
  /** #19934 — số vào phòng bắt đầu từ; null = 1. */
  roomStartNumber?: number | null;
  /** #19934 — số tiếp đón bắt đầu từ; null = 1. */
  registrationStartNumber?: number | null;
}

export const bookingSettingsApi = {
  get(): Promise<BookingSettings> {
    return apiClient
      .get<BookingSettings>("/v1/clinic/settings/booking")
      .then((r) => r.data);
  },
  update(body: BookingSettings): Promise<BookingSettings> {
    return apiClient
      .put<BookingSettings>("/v1/clinic/settings/booking", body)
      .then((r) => r.data);
  },
  /** Per-branch self-booking switch (cho phép bệnh nhân đặt lịch trực tuyến). */
  getOnlineBooking(): Promise<boolean> {
    return apiClient
      .get<{ enabled: boolean }>("/v1/clinic/settings/booking/online-booking")
      .then((r) => r.data.enabled);
  },
  setOnlineBooking(enabled: boolean): Promise<boolean> {
    return apiClient
      .put<{ enabled: boolean }>("/v1/clinic/settings/booking/online-booking", { enabled })
      .then((r) => r.data.enabled);
  },
};

/**
 * #19936 — sức chứa riêng của từng phòng (tối đa lượt/NGÀY, cộng dồn mọi buổi) — nơi DUY NHẤT
 * khai sức chứa. `branchId` = chi nhánh đang áp; phòng không có trong `limits` = không giới hạn.
 */
export interface RoomLimits {
  branchId: string | null;
  limits: { roomId: string; maxPerDay: number }[];
}

export const roomLimitsApi = {
  get(): Promise<RoomLimits> {
    return apiClient
      .get<RoomLimits>("/v1/clinic/settings/booking/room-limits")
      .then((r) => r.data);
  },
  /** Thay toàn bộ danh sách của chi nhánh đang áp. */
  update(limits: { roomId: string; maxPerDay: number }[]): Promise<RoomLimits> {
    return apiClient
      .put<RoomLimits>("/v1/clinic/settings/booking/room-limits", { limits })
      .then((r) => r.data);
  },
};

/**
 * #19939 — đặt lịch online có thật sự mở giờ nào trong {@code horizonDays} ngày tới không (quét bằng
 * chính availability), và các luật đang áp, đọc từ cấu hình thật.
 */
export interface BookingReadiness {
  onlineBookingEnabled: boolean;
  horizonDays: number;
  openDays: number;
  firstOpenDate: string | null;
  /** Đang bật đặt online nhưng không có ngày nào còn khung — TC08. */
  noOpenHours: boolean;
  /** mã lý do → số ngày bị đóng vì lý do đó. */
  closedReasons: Record<string, number>;
  /** mã lý do → một câu mẫu. */
  closedMessages: Record<string, string>;
  sessionsConfigured: boolean;
  rules: { code: string; text: string }[];
}

export const bookingReadinessApi = {
  get(): Promise<BookingReadiness> {
    return apiClient
      .get<BookingReadiness>("/v1/clinic/settings/booking/readiness")
      .then((r) => r.data);
  },
};

/** #19939 — một ngày của availability (cùng hợp đồng kênh bệnh nhân), cho màn nhân viên. */
export interface DayAvailability {
  mode: "SLOT" | "SESSION";
  sessions: { code: string; startTime: string; endTime: string; full: boolean; reason?: string | null }[] | null;
  slots: { startTime: string; remaining: number | null; full: boolean; reason?: string | null }[] | null;
  /** Lý do cả ngày không đặt được; null = ngày mở. */
  closed: { code: string; message: string } | null;
}

export const bookingAvailabilityApi = {
  day(params: {
    date: string;
    departmentId?: string | null;
    roomId?: string | null;
    doctorId?: string | null;
  }): Promise<DayAvailability> {
    const q: Record<string, string> = { date: params.date };
    if (params.departmentId) q.departmentId = params.departmentId;
    if (params.roomId) q.roomId = params.roomId;
    if (params.doctorId) q.doctorId = params.doctorId;
    return apiClient
      .get<DayAvailability>("/v1/booking/availability", { params: q })
      .then((r) => r.data);
  },
};

export const bookingSettingsQueryKeys = {
  all: () => ["clinic-booking-settings"] as const,
  roomLimits: () => ["clinic-booking-settings", "room-limits"] as const,
  readiness: () => ["clinic-booking-settings", "readiness"] as const,
  day: (date: string, departmentId?: string | null, roomId?: string | null, doctorId?: string | null) =>
    ["booking-availability", date, departmentId ?? null, roomId ?? null, doctorId ?? null] as const,
};
